import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Users, Search, Plus, X, Building2, Mail, Phone, MapPin, Check, AlertCircle } from 'lucide-react';
import { clientsAPI } from '../../../../utils/clientsAPI';
import { logger } from '../../../../utils/logger';
import type { Client } from '../../../../types/models';

interface EditableClientSectionProps {
  data: any;
  onUpdate: (field: any, value: string) => void;
  onSelectClient?: (client: any) => void;
  accentColor: string;
}

interface QuickClientFormData {
  type: 'INDIVIDUAL' | 'COMPANY';
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  addressLine1: string;
  city: string;
  zipCode: string;
  countryCode: string;
}

/**
 * Section client avec sélection obligatoire
 * - Recherche de clients existants
 * - Affichage en lecture seule des infos client
 * - Modal de création rapide de client
 */
export const EditableClientSection: React.FC<EditableClientSectionProps> = ({
  data,
  onUpdate,
  onSelectClient,
  accentColor,
}) => {
  const { t } = useTranslation();
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  // Formulaire de création rapide
  const [quickFormData, setQuickFormData] = useState<QuickClientFormData>({
    type: 'COMPANY',
    fullName: '',
    companyName: '',
    email: '',
    phone: '',
    addressLine1: '',
    city: '',
    zipCode: '',
    countryCode: 'FR',
  });

  // Client actuellement sélectionné
  const selectedClientId = data.clientId;
  const hasSelectedClient = !!selectedClientId && !!data.clientName;

  // Charger la liste des clients
  useEffect(() => {
    const loadClients = async () => {
      setIsLoadingClients(true);
      try {
        const result = await clientsAPI.getAll({ limit: 100 });
        if (Array.isArray(result.clients)) {
          setClients(result.clients);
        } else if (Array.isArray(result)) {
          setClients(result);
        } else {
          logger.warn('Format de reponse clients inattendu:', result);
          setClients([]);
        }
      } catch (error) {
        logger.error('Erreur chargement clients:', error);
        setClients([]);
      } finally {
        setIsLoadingClients(false);
      }
    };
    loadClients();
  }, []);

  // Filtrer les clients par recherche
  const filteredClients = useMemo(() => {
    if (!searchTerm.trim()) return clients;

    const term = searchTerm.toLowerCase();
    return clients.filter(client => {
      const name = (client.fullName || client.companyName || '').toLowerCase();
      const company = (client.companyName || '').toLowerCase();
      const email = (client.email || '').toLowerCase();
      return name.includes(term) || company.includes(term) || email.includes(term);
    });
  }, [clients, searchTerm]);

  // Sélectionner un client
  const handleSelectClient = (client: Client) => {
    if (onSelectClient) {
      onSelectClient(client);
    }
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  // Effacer la sélection
  const handleClearSelection = () => {
    onUpdate('clientId', '');
    onUpdate('clientName', '');
    onUpdate('clientCompany', '');
    onUpdate('clientEmail', '');
    onUpdate('clientPhone', '');
    onUpdate('clientAddressLine1', '');
    onUpdate('clientCity', '');
    onUpdate('clientZipCode', '');
    onUpdate('clientCountry', 'FR');
    onUpdate('clientVatNumber', '');
  };

  // Créer un nouveau client
  const handleCreateClient = async () => {
    if (!quickFormData.fullName.trim()) {
      return;
    }

    setIsCreating(true);
    try {
      const newClient = await clientsAPI.create({
        type: quickFormData.type,
        fullName: quickFormData.fullName,
        companyName: quickFormData.companyName || undefined,
        email: quickFormData.email || undefined,
        phone: quickFormData.phone || undefined,
        addressLine1: quickFormData.addressLine1 || undefined,
        city: quickFormData.city || undefined,
        zipCode: quickFormData.zipCode || undefined,
        countryCode: quickFormData.countryCode,
      });

      // Ajouter à la liste locale
      setClients(prev => [newClient, ...prev]);

      // Sélectionner automatiquement le nouveau client
      handleSelectClient(newClient);

      // Fermer le modal et reset le formulaire
      setShowCreateModal(false);
      setQuickFormData({
        type: 'COMPANY',
        fullName: '',
        companyName: '',
        email: '',
        phone: '',
        addressLine1: '',
        city: '',
        zipCode: '',
        countryCode: 'FR',
      });
    } catch (error: any) {
      logger.error('Erreur creation client:', error);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-bold uppercase tracking-wide mb-3" style={{ color: accentColor }}>
        {t('templates.client.clientRequired')}
      </h3>

      {/* Client sélectionné - Affichage en lecture seule */}
      {hasSelectedClient ? (
        <div className="p-4 bg-teal-50/50 backdrop-blur-sm border border-teal-200/50 rounded-xl">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-teal-100 rounded-lg">
                <Check className="w-4 h-4 text-teal-600" />
              </div>
              <span className="text-sm font-medium text-teal-800">{t('templates.client.selectedClient')}</span>
            </div>
            <button
              type="button"
              onClick={handleClearSelection}
              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title={t('templates.client.changeClient')}
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Infos client en lecture seule */}
          <div className="space-y-2 text-sm">
            <div className="flex items-center gap-2 text-gray-900 font-medium">
              <Users className="w-4 h-4 text-teal-600" />
              {data.clientName}
            </div>
            {data.clientCompany && (
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="w-4 h-4 text-gray-400" />
                {data.clientCompany}
              </div>
            )}
            {data.clientEmail && (
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                {data.clientEmail}
              </div>
            )}
            {data.clientPhone && (
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                {data.clientPhone}
              </div>
            )}
            {(data.clientAddressLine1 || data.clientCity) && (
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                <span>
                  {data.clientAddressLine1}
                  {data.clientAddressLine1 && data.clientCity && ', '}
                  {data.clientZipCode} {data.clientCity}
                </span>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Sélection de client */
        <div className="space-y-3">
          {/* Message d'alerte */}
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-800 text-sm">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{t('templates.client.selectClientPrompt')}</span>
          </div>

          {/* Barre de recherche */}
          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsDropdownOpen(true);
                }}
                onFocus={() => setIsDropdownOpen(true)}
                className="w-full pl-10 pr-4 py-2.5 bg-white/40 backdrop-blur-sm border border-white/40 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white/60 transition-all duration-300"
                placeholder={t('templates.client.searchClient')}
              />
            </div>

            {/* Dropdown des résultats */}
            {isDropdownOpen && (
              <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 max-h-64 overflow-y-auto">
                {isLoadingClients ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {t('templates.client.loading')}
                  </div>
                ) : filteredClients.length === 0 ? (
                  <div className="p-4 text-center text-gray-500 text-sm">
                    {searchTerm ? t('templates.client.noClientFound') : t('templates.client.noClient')}
                  </div>
                ) : (
                  filteredClients.map((client) => (
                    <button
                      key={client.id}
                      type="button"
                      onClick={() => handleSelectClient(client)}
                      className="w-full px-4 py-3 text-left hover:bg-teal-50 transition-colors border-b border-gray-100 last:border-b-0"
                    >
                      <div className="font-medium text-gray-900">
                        {client.fullName || client.companyName}
                      </div>
                      {client.companyName && (
                        <div className="text-sm text-gray-500">{client.companyName}</div>
                      )}
                      {client.email && (
                        <div className="text-xs text-gray-400">{client.email}</div>
                      )}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Bouton créer client */}
          <button
            type="button"
            onClick={() => setShowCreateModal(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white/60 hover:bg-teal-50 border border-dashed border-teal-300 rounded-xl text-teal-700 font-medium transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('templates.client.createNewClient')}
          </button>
        </div>
      )}

      {/* Fermer dropdown si clic ailleurs */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      {/* Modal création rapide client */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold text-gray-900">{t('templates.client.newClient')}</h3>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Form */}
            <div className="p-4 space-y-4">
              {/* Type */}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setQuickFormData(prev => ({ ...prev, type: 'COMPANY' }))}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    quickFormData.type === 'COMPANY'
                      ? 'bg-teal-100 text-teal-700 border-2 border-teal-500'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                  }`}
                >
                  <Building2 className="w-4 h-4 mx-auto mb-1" />
                  {t('templates.client.business')}
                </button>
                <button
                  type="button"
                  onClick={() => setQuickFormData(prev => ({ ...prev, type: 'INDIVIDUAL' }))}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                    quickFormData.type === 'INDIVIDUAL'
                      ? 'bg-teal-100 text-teal-700 border-2 border-teal-500'
                      : 'bg-gray-100 text-gray-600 border-2 border-transparent'
                  }`}
                >
                  <Users className="w-4 h-4 mx-auto mb-1" />
                  {t('templates.client.individual')}
                </button>
              </div>

              {/* Nom */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {quickFormData.type === 'COMPANY' ? t('templates.client.contactName') : t('templates.client.fullName')}
                </label>
                <input
                  type="text"
                  value={quickFormData.fullName}
                  onChange={(e) => setQuickFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder={t('templates.client.placeholders.fullName')}
                />
              </div>

              {/* Société (si entreprise) */}
              {quickFormData.type === 'COMPANY' && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('templates.client.companyName')}
                  </label>
                  <input
                    type="text"
                    value={quickFormData.companyName}
                    onChange={(e) => setQuickFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder={t('templates.client.placeholders.companyName')}
                  />
                </div>
              )}

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('templates.client.emailLabel')}
                </label>
                <input
                  type="email"
                  value={quickFormData.email}
                  onChange={(e) => setQuickFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="client@exemple.fr"
                />
              </div>

              {/* Téléphone */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('templates.client.phoneLabel')}
                </label>
                <input
                  type="tel"
                  value={quickFormData.phone}
                  onChange={(e) => setQuickFormData(prev => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="01 23 45 67 89"
                />
              </div>

              {/* Adresse */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('templates.client.addressLabel')}
                </label>
                <input
                  type="text"
                  value={quickFormData.addressLine1}
                  onChange={(e) => setQuickFormData(prev => ({ ...prev, addressLine1: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  placeholder="123 rue de la Paix"
                />
              </div>

              {/* Ville + CP */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('templates.client.zipCodeLabel')}
                  </label>
                  <input
                    type="text"
                    value={quickFormData.zipCode}
                    onChange={(e) => setQuickFormData(prev => ({ ...prev, zipCode: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="75001"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    {t('templates.client.cityLabel')}
                  </label>
                  <input
                    type="text"
                    value={quickFormData.city}
                    onChange={(e) => setQuickFormData(prev => ({ ...prev, city: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder={t('templates.client.placeholders.city')}
                  />
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex gap-3 p-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-xl transition-colors"
              >
                {t('templates.client.cancelCreate')}
              </button>
              <button
                type="button"
                onClick={handleCreateClient}
                disabled={!quickFormData.fullName.trim() || isCreating}
                className="flex-1 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCreating ? t('templates.client.creatingClient') : t('templates.client.createAndSelect')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
